class GuardianCardPopup {
  constructor(popup) {
    this.popup = popup;
    this.currentStep = 1;
    this.selectedCard = null;

    this.steps = popup.querySelectorAll('[data-guardian-step]');
    this.cardButtons = popup.querySelectorAll('[data-guardian-card]');
    this.previewCard = popup.querySelector('[data-guardian-preview-card]');
    this.previewFront = popup.querySelector('[data-guardian-preview-front]');
    this.previewBack = popup.querySelector('[data-guardian-preview-back]');
    this.revealCard = popup.querySelector('[data-guardian-reveal-card]');
    this.revealFront = popup.querySelector('[data-guardian-reveal-front]');
    this.revealBack = popup.querySelector('[data-guardian-reveal-back]');
    this.blessingText = popup.querySelector('[data-guardian-blessing-text]');
    this.form = popup.querySelector('#GuardianContactPopup');
    this.errorEl = popup.querySelector('[data-guardian-error]');
    this.flipBtn = popup.querySelector('[data-guardian-flip-btn]');
    this.copyBtn = popup.querySelector('[data-guardian-copy-code]');
    this.codeEl = popup.querySelector('[data-guardian-code]');
    this.continueBtn = popup.querySelector('[data-guardian-continue]');
    this.expireDays = Math.max(1, parseInt(popup.getAttribute('data-expire'), 10) || 1);

    this.init();
  }

  init() {
    this.cardButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.selectCard(btn));
    });

    // 禁止原生提交（否则会整页刷新并弹出「订阅成功」小窗）
    if (this.form) {
      this.form.setAttribute('onsubmit', 'return false');
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.handleSubmit(e);
      });
    }

    if (this.flipBtn) {
      this.flipBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleSubmit(e);
      });
    }

    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', () => this.copyCode());
    }

    if (this.continueBtn) {
      this.continueBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // 翻牌刚结束时忽略误触，避免自动关掉折扣展示
        if (this.popup.dataset.guardianRevealLock === 'true') {
          return;
        }
        this.rememberDismissed();
        this.closePopup();
        // 仅关闭弹窗，不跳转，方便用户留在当前页使用折扣码
      });
    }

    // 叉号关闭时写入关闭状态（与 NewsletterPopup 关闭逻辑配合）
    this.popup.querySelector('[data-close-newsletter-popup]')?.addEventListener('click', () => {
      this.rememberDismissed();
      this.hideSuccessModal();
    });
  }

  selectCard(btn) {
    this.selectedCard = {
      id: btn.dataset.guardianCard,
      label: btn.dataset.cardLabel,
      blessing: btn.dataset.cardBlessing,
      image: btn.dataset.cardImage || ''
    };

    this.populateCardFace(this.previewFront, this.selectedCard);
    this.populateCardFace(this.previewBack, this.selectedCard);
    this.goToStep(2);
  }

  populateCardFace(container, card) {
    if (!container || !card) return;

    container.replaceChildren();

    if (card.image) {
      const img = document.createElement('img');
      img.className = 'guardian-card__bg';
      img.src = card.image;
      img.alt = card.label || '';
      img.width = 400;
      img.height = 600;
      img.loading = 'lazy';
      container.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'guardian-card__back-placeholder';
      const span = document.createElement('span');
      span.textContent = card.label || '';
      placeholder.appendChild(span);
      container.appendChild(placeholder);
    }
  }

  goToStep(step) {
    this.currentStep = step;
    this.steps.forEach((el) => {
      const isActive = parseInt(el.dataset.guardianStep, 10) === step;
      el.classList.toggle('is-active', isActive);
      el.hidden = !isActive;
    });
  }

  getContactUrl() {
    if (this.form?.action) {
      return this.form.action.split('#')[0];
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root.replace(/\/?$/, '/')}contact`;
  }

  buildFormData() {
    const formData = new FormData(this.form);
    if (!formData.get('contact[tags]')) {
      formData.set('contact[tags]', 'newsletter,guardian-card');
    }
    if (!formData.get('contact[accepts_marketing]')) {
      formData.set('contact[accepts_marketing]', 'true');
    }
    return formData;
  }

  async submitEmail() {
    const url = this.getContactUrl();

    // Attempt 1: JSON (when storefront supports it)
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: this.buildFormData(),
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok || response.status === 0 || (response.status >= 300 && response.status < 400)) {
        const data = await response.json().catch(() => null);
        // Only treat non-empty error maps as hard validation failures
        if (data && data.status === 422) return false;
        if (data && data.errors && typeof data.errors === 'object' && Object.keys(data.errors).length) {
          return false;
        }
        return true;
      }
    } catch (e) {
      // fall through
    }

    // Attempt 2: HTML + manual redirect (Shopify classic form post)
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: this.buildFormData(),
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        redirect: 'manual'
      });

      if (
        response.type === 'opaqueredirect' ||
        response.status === 0 ||
        (response.status >= 200 && response.status < 400)
      ) {
        return true;
      }
    } catch (e) {
      // fall through
    }

    // Attempt 3: image beacon / keepalive — fire-and-forget for flaky mobile networks
    try {
      if (navigator.sendBeacon) {
        const ok = navigator.sendBeacon(url, this.buildFormData());
        if (ok) return true;
      }
    } catch (e) {
      // ignore
    }

    return false;
  }

  async handleSubmit(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!this.form || !this.flipBtn || this.flipBtn.disabled) return;

    const emailInput = this.form.querySelector('#GuardianNewsletterEmail');
    const email = emailInput?.value?.trim();

    if (!email) {
      this.showError('Please enter your email address.');
      emailInput?.focus();
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError('Please enter a valid email address.');
      emailInput?.focus();
      return;
    }

    this.hideError();
    this.hideSuccessModal();
    this.flipBtn.disabled = true;
    const originalText = this.flipBtn.dataset.originalText || this.flipBtn.textContent;
    this.flipBtn.dataset.originalText = originalText;
    this.flipBtn.textContent = '...';

    const formArea = this.form.closest('.guardian-step__form-area');
    if (formArea) formArea.style.opacity = '0.4';
    if (this.previewCard) this.previewCard.classList.add('is-flipping');

    // Best-effort subscribe — never block the flip UX on API quirks / theme editor / mobile
    try {
      await this.submitEmail();
    } catch (err) {
      console.warn('[GuardianCardPopup] subscribe error:', err);
    }

    // 再次挡住主题里可能弹出的「订阅成功」小窗
    this.hideSuccessModal();

    try {
      await this.playFlipAnimation();
      this.showReward();
    } catch (err) {
      console.warn('[GuardianCardPopup] flip error:', err);
      this.showReward();
    }
  }

  async playFlipAnimation() {
    if (this.revealFront) {
      this.populateCardFace(this.revealFront, this.selectedCard);
    }
    if (this.revealBack) {
      this.populateCardFace(this.revealBack, this.selectedCard);
    }

    const formArea = this.form?.closest('.guardian-step__form-area');

    if (!this.previewCard) {
      if (formArea) formArea.hidden = true;
      return;
    }

    return new Promise((resolve) => {
      setTimeout(() => {
        if (formArea) formArea.hidden = true;
        this.previewCard.classList.remove('is-flipping');
        this.previewCard.classList.add('is-flipped');
        resolve();
      }, 700);
    });
  }

  showReward() {
    if (this.blessingText && this.selectedCard) {
      this.blessingText.textContent = this.selectedCard.blessing;
    }

    this.goToStep(3);
    this.hideSuccessModal();

    // 已完成订阅翻牌：立刻记住关闭，避免刷新/跳转后再次自动弹出
    // 注意：只写关闭状态，不关掉当前弹窗 UI
    this.rememberDismissed();

    // 锁定较长时间，防止翻牌后误触「继续」导致立刻关掉
    this.popup.dataset.guardianRevealLock = 'true';
    this.popup.dataset.guardianStep = '3';
    if (this.continueBtn) {
      this.continueBtn.style.pointerEvents = 'none';
    }

    window.setTimeout(() => {
      delete this.popup.dataset.guardianRevealLock;
      if (this.continueBtn) {
        this.continueBtn.style.pointerEvents = '';
      }
    }, 2000);

    // 持续压制主题「订阅成功」小窗（有些主题会延迟弹出）
    this.hideSuccessModal();
    window.setTimeout(() => this.hideSuccessModal(), 300);
    window.setTimeout(() => this.hideSuccessModal(), 1000);
  }

  hideSuccessModal() {
    const modal = document.querySelector('.newsletter-success-modal');
    if (modal) {
      modal.style.display = 'none';
      modal.style.visibility = 'hidden';
      modal.style.opacity = '0';
      modal.style.pointerEvents = 'none';
    }
    document.body.classList.remove('newsletter-message-show');
    const messagePopup = document.getElementById('halo-newsletter-message-popup');
    if (messagePopup) {
      messagePopup.style.display = 'none';
    }
  }

  showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  hideError() {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  copyCode() {
    if (!this.codeEl) return;

    const code = this.codeEl.textContent.trim();
    const done = () => {
      this.copyBtn.classList.add('is-copied');
      setTimeout(() => this.copyBtn.classList.remove('is-copied'), 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => {
        this.fallbackCopy(code, done);
      });
    } else {
      this.fallbackCopy(code, done);
    }
  }

  fallbackCopy(code, done) {
    const input = document.createElement('input');
    input.value = code;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    try {
      document.execCommand('copy');
      done();
    } catch (e) {
      // ignore
    }
    document.body.removeChild(input);
  }

  rememberDismissed() {
    // 优先复用 NewsletterPopup 的 cookie + localStorage 逻辑
    if (typeof this.popup.persistDismissed === 'function') {
      this.popup.persistDismissed(this.expireDays);
      if (typeof this.popup.clearShowTimers === 'function') {
        this.popup.clearShowTimers();
      }
      return;
    }

    const expireDays = this.expireDays || 1;
    const d = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    document.cookie = 'newsletter-popup=closed;expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
    try {
      localStorage.setItem('newsletter-popup-closed', JSON.stringify({
        until: d.getTime()
      }));
    } catch (e) {
      // ignore
    }
  }

  closePopup() {
    if (typeof this.popup.clearShowTimers === 'function') {
      this.popup.clearShowTimers();
    }
    this.hideSuccessModal();
    document.body.classList.remove('newsletter-show');
    setTimeout(() => {
      document.body.classList.remove('show-newsletter-image');
    }, 700);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const popup = document.getElementById('halo-guardian-card-popup');
  if (popup) {
    const flipBtn = popup.querySelector('[data-guardian-flip-btn]');
    if (flipBtn) {
      flipBtn.dataset.originalText = flipBtn.textContent.trim();
    }
    new GuardianCardPopup(popup);
  }
});
